import { Timestamp } from 'firebase/firestore';

/**
 * PAGOS DE UNA ORDEN — `payments[]`, la ÚNICA fuente de cómo se pagó.
 *
 * Los 3 escalares viejos (`cashAmount`/`creditCardAmount`/`bankTransferAmount`) se
 * RETIRARON en la F6 del plan (2026-10): ya no se escriben ni se leen. Las órdenes
 * viejas los conservan como fósil. El 2026-10-08 se completó `payments[]` en toda
 * orden que no lo traía, con lo mismo que derivaba esta capa.
 *
 * Plan: `../PLAN-MODELO-PAGOS-Y-ORIGEN-ORDENES.md` (§2 el modelo, §4 esta capa).
 *
 * ⚠️ **RÉPLICA DE PARIDAD admin ↔ web**, con suites gemelas. Cambiar una exige
 * cambiar la otra (misma regla que PointsHelper, ShortId, TipHelper,
 * normalizePhone, Availability y OrderLineBuilder).
 *
 * **REGLA: cómo se pagó se lee SIEMPRE por aquí** (`getOrderPaidBy` y compañía),
 * nunca recorriendo `order.payments` a mano en una pantalla.
 *
 * Invariante del modelo nuevo, en una orden totalmente cobrada:
 *
 *     Σ payments[].amount  ===  total + (tipAmount || 0)
 *
 * (la propina NO es venta y no vive aquí: es `Order.tipAmount` — D5 del plan y
 * D2 del plan del espejo. Los montos de pago SÍ la incluyen, porque es dinero
 * que entra físicamente al cajón.)
 */

export enum PaymentMethod {
    Cash = 'Cash',
    CreditCard = 'CreditCard',
    BankTransfer = 'BankTransfer'
}

export interface OrderPayment {
    method: PaymentMethod;
    /** Lo que ENTRÓ por este medio, con la propina que le toque. Siempre MXN. */
    amount: number;
    /** Cuándo se cobró — es lo que el corte necesita para caer en la ventana del turno correcto. */
    date: Timestamp | Date;
    /**
     * Día LOCAL `yyyy-MM-dd`. Comodidad para agrupar EN CLIENTE.
     * ⚠️ NO es consultable: Firestore no consulta dentro de arreglos de objetos (D2 del plan).
     */
    day: string;
    /** La caja abierta en ese momento. Sin esto el corte tiene que atribuir por ventana de tiempo. */
    cashRegisterId?: string;
    /** Quién cobró — puede NO ser quien creó el pedido (cambio de turno en una mesa). */
    collaboratorId?: string;
    /** Referencia externa: paymentIntent de Stripe, autorización de la terminal. */
    reference?: string;
}

/** Con qué app y qué build nació la orden. Lo escribe QUIEN LA CREA; jamás se recalcula. */
export interface OrderCreatedWith {
    app: 'admin' | 'web' | 'server';
    surface: 'pos' | 'checkout' | 'kiosk' | 'table' | 'stripe-webhook' | 'table-consolidation';
    /** Versión del build (`REACT_APP_VERSION` en los frontends). `'inferido'` en las migradas. */
    version: string;
}

/**
 * Una fecha como puede llegar de Firestore: `Timestamp`, `Date`, el objeto plano
 * `{seconds}` que quedó serializado en docs viejos, o un string/número.
 *
 * Existe para que estos helpers no usen `any`: la web compila con ESLint estricto
 * (`no-explicit-any` es ERROR fuera de los tests) y un `any` aquí ROMPÍA su build
 * — y como `deploy.sh` no abortaba, el deploy subía el bundle anterior en silencio.
 */
export type FirestoreDateLike = Timestamp | Date | string | number | { seconds?: number; toDate?: () => Date };

/** La vista mínima que necesitan estos helpers — así sirven igual en admin, web y scripts. */
export interface PayableOrder {
    payments?: OrderPayment[];
    total?: number;
    tipAmount?: number;
}

/**
 * Fecha de un doc de Firestore en sus TRES formas posibles.
 *
 * ⚠️ **Se queda aquí a propósito, y es la única copia que sobrevive a la
 * consolidación del 2026-09-11** (donde las doce implementaciones del admin
 * pasaron a `toMillisSafe`/`toDateSafe` de `@utils/Date`). Este módulo es de
 * PARIDAD admin↔web: tiene que ser réplica exacta en los dos repos, y sus
 * rutas de utils no coinciden — importar una de ellas lo haría divergir. Si
 * cambias esto, cámbialo en el otro repo.
 */
export const toPaymentDate = (value?: FirestoreDateLike | null): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return isNaN(value.getTime()) ? undefined : value;
    if (typeof value === 'object' && typeof value.toDate === 'function') {
        const d = value.toDate();
        return isNaN(d.getTime()) ? undefined : d;
    }
    if (typeof value === 'object' && typeof value.seconds === 'number') return new Date(value.seconds * 1000);
    const d = new Date(value as string | number);
    return isNaN(d.getTime()) ? undefined : d;
};

/** Día LOCAL `yyyy-MM-dd`. Local y no UTC: el corte del negocio es en hora de México. */
export const toPaymentDay = (value?: FirestoreDateLike | null): string => {
    const date = toPaymentDate(value) || new Date();
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const dia = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${mes}-${dia}`;
};

/** Lo que hace falta para armar los renglones de pago de una orden. */
export interface BuildPaymentsInput {
    cashAmount?: number;
    creditCardAmount?: number;
    bankTransferAmount?: number;
    /**
     * Cuándo se cobró. En una venta nueva es AHORA, no la fecha del pedido.
     * Admite `undefined` —y sigue siendo obligatorio pasarlo— porque corregir un
     * importe a mano usa la fecha de la orden, que puede no venir; en ese caso
     * `buildOrderPayments` cae a la de este momento.
     */
    date: FirestoreDateLike | undefined;
    cashRegisterId?: string;
    collaboratorId?: string;
    reference?: string;
}

/**
 * Arma `payments[]` desde los importes de cada medio.
 *
 * ⚠️ **Es la ÚNICA forma de construir el arreglo**: así todos los escritores (POS,
 * cobro de pendientes, corrección a mano) arman los renglones igual — orden,
 * claves opcionales omitidas, día local.
 *
 * Un importe en 0 (o ausente) no es un pago. Un importe NEGATIVO sí se conserva:
 * el corte lo suma tal cual y filtrarlo movería el total.
 */
export const buildOrderPayments = (input: BuildPaymentsInput): OrderPayment[] => {
    const date = toPaymentDate(input.date) || new Date();
    const day = toPaymentDay(input.date);
    const payments: OrderPayment[] = [];
    const add = (method: PaymentMethod, amount?: number) => {
        if (!amount) return; // 0, undefined y null no son un pago
        const payment: OrderPayment = { method, amount, date, day };
        // Las claves ausentes se OMITEN en vez de ir en `undefined`: Firestore
        // rechaza undefined dentro de un arreglo aunque el cliente tenga
        // `ignoreUndefinedProperties` (esa opción no entra a los elementos).
        if (input.cashRegisterId) payment.cashRegisterId = input.cashRegisterId;
        if (input.collaboratorId) payment.collaboratorId = input.collaboratorId;
        if (input.reference) payment.reference = input.reference;
        payments.push(payment);
    };
    add(PaymentMethod.Cash, input.cashAmount);
    add(PaymentMethod.CreditCard, input.creditCardAmount);
    add(PaymentMethod.BankTransfer, input.bankTransferAmount);
    return payments;
};

/**
 * Los pagos de una orden: su `payments[]`. Vacío = no se ha cobrado; AUSENTE = no
 * se sabe (solo 12 órdenes de jun-2023, que nunca guardaron cómo se pagaron) y se
 * lee como nada registrado. Los escalares viejos ya no se consultan (F6).
 */
export const getOrderPayments = (order?: PayableOrder): OrderPayment[] =>
    order && Array.isArray(order.payments) ? order.payments : [];

/**
 * Con qué app y build se está creando la orden. Cada app pasa lo suyo: los
 * valores difieren por repo, la FORMA no.
 */
export const buildCreatedWith = (
    app: OrderCreatedWith['app'],
    surface: OrderCreatedWith['surface'],
    version?: string
): OrderCreatedWith => ({ app, surface, version: version || 'desconocida' });

/** Lo cobrado por un medio. Reemplaza `order.cashAmount` y sus hermanos en TODOS los lectores. */
export const getOrderPaidBy = (order: PayableOrder | undefined, method: PaymentMethod): number =>
    getOrderPayments(order).reduce((total, payment) => (payment.method === method ? total + payment.amount : total), 0);

/** Σ de lo cobrado por todos los medios (propina incluida, porque entró al cajón). */
export const getOrderPaidTotal = (order?: PayableOrder): number =>
    getOrderPayments(order).reduce((total, payment) => total + payment.amount, 0);

/** Lo que se esperaba cobrar: el total de la venta más la propina. */
export const getOrderExpectedTotal = (order?: PayableOrder): number => (order?.total || 0) + (order?.tipAmount || 0);

/**
 * ¿Quedó completamente cobrada? Con tolerancia de un centavo: los porcentajes de
 * promoción producen fracciones y comparar flotantes con `===` daría falsos.
 */
export const isOrderFullyPaid = (order?: PayableOrder): boolean =>
    Math.abs(getOrderPaidTotal(order) - getOrderExpectedTotal(order)) < 0.01;

/** Los medios con los que se cobró, sin repetir y en orden de aparición. */
export const getOrderPaymentMethods = (order?: PayableOrder): PaymentMethod[] => {
    const seen: PaymentMethod[] = [];
    getOrderPayments(order).forEach((payment) => {
        if (!seen.includes(payment.method)) seen.push(payment.method);
    });
    return seen;
};
